import { motion } from 'framer-motion'
import React, { FC, useState } from 'react'

import ImageByWidthDevice from '@/components/ImageByWidthDevice'
import { introBottomVariants } from '@/config/const'
import { ImageResponsive } from '@/types/common'
import { InfographicPopulation } from '@/types/why-sez'
import clsxm from '@/utils/clsxm'

const MapContent: FC<{
  data: {
    title?: string
    description?: string
    images?: ImageResponsive
    borderColor: string
    cardIcon?: string
    cardIconStyle?: string
    cardIconShadow?: string
    cardIconShadowStyle?: string
    imageContent?: ImageResponsive
    spaceContentStyle: string
    cardContainerStyle?: string
    population?: {
      number: string | number
      unitNumber: string
      items: InfographicPopulation['items']
      cardImageFiles?: ImageResponsive
    }
    strategicLocation?: {
      cardImageFiles?: ImageResponsive
      contentImageFiles?: ImageResponsive[]
    }
    naturalResources?: {
      cardImageFiles?: ImageResponsive
    }
    legendImageFiles?: ImageResponsive
  }
  active: boolean
}> = ({
  active,
  data: {
    cardIconShadow,
    cardIconShadowStyle,
    cardIcon,
    cardIconStyle,
    title,
    description,
    borderColor,
    imageContent,
    cardContainerStyle,
    spaceContentStyle,
    population,
    strategicLocation,
    naturalResources,
    legendImageFiles,
  },
}) => {
  const [openAccordion, setOpenAccordion] = useState('0')
  return (
    <>
      <motion.div
        className={clsxm('map-card-position shadow-timeline-card', active ? 'block' : 'hidden')}
        initial='hide'
        whileInView='show'
        exit='hide'
        variants={introBottomVariants}
        viewport={{ once: true }}
      >
        <div className={clsxm('map-card-container', cardContainerStyle)}>
          <div className={clsxm('card-border-small', borderColor)} />
          <div className={clsxm('card-border-big', borderColor)} />

          {cardIconShadow && (
            <div className={cardIconShadowStyle}>
              <div className='absolute inset-0' />
              <img
                src={cardIconShadow}
                alt='Card Icon Shadow'
                className='opacity-10'
              />
            </div>
          )}

          <div className={clsxm('relative z-10 px-3 md:px-4 lg:px-6', cardContainerStyle)}>
            <div className={clsxm('flex flex-col', spaceContentStyle)}>
              {cardIcon && (
                <div className='flex-0 lg:flex-0 relative md:flex-1'>
                  <div className='absolute inset-0' />
                  <img
                    src={cardIcon}
                    alt='Card Icon'
                    className={cardIconStyle}
                  />
                </div>
              )}

              {title && <h5 className='map-card-title font-extrabold leading-normal text-red'>{title}</h5>}

              {population?.number && population?.unitNumber && (
                <p className='leading-10 text-gray-1'>
                  <span className='text-[55px] font-[900] uppercase leading-[normal] text-red'>
                    {population.number ?? 0}
                  </span>
                  {population.unitNumber ?? ''}
                </p>
              )}

              {description && (
                <div
                  className='map-card-description tracking-[-0.176px] text-gray-2'
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}

              {imageContent && (
                <ImageByWidthDevice
                  images={imageContent}
                  alt='World Map'
                  className='rounded-lg pb-6'
                />
              )}

              {population?.items && population?.items.length > 0 && (
                <div className='flex flex-col gap-4 pb-6 md:pb-0'>
                  {population?.items.map((item: any) => (
                    <div
                      key={item.xid}
                      className={clsxm(
                        'flex gap-4 rounded-lg bg-[#f7f7f7]/40 p-3 backdrop-blur-[21px] transition-all duration-300',
                        openAccordion == item.xid ? 'items-start' : 'items-center'
                      )}
                    >
                      <ImageByWidthDevice
                        images={item.iconFiles}
                        alt={item.title}
                        className='size-[38px]'
                      />

                      <div className='flex flex-col'>
                        <div className='flex w-full items-center justify-between gap-4'>
                          <p className='font-medium leading-normal text-gray-1'>{item.title}</p>

                          <button
                            type='button'
                            onClick={() => setOpenAccordion(item.xid)}
                          >
                            <span
                              className={clsxm(
                                'text-2xl transition-all duration-300',
                                openAccordion == item.xid ? 'kek-arrow-up-s-line' : 'kek-arrow-down-s-line'
                              )}
                            />
                          </button>
                        </div>

                        <div
                          className={clsxm(
                            'overflow-hidden transition-all duration-300',
                            openAccordion == item.xid ? 'max-h-none pt-2 opacity-100 lg:pt-4' : 'max-h-0 pt-0 opacity-0'
                          )}
                        >
                          <div
                            className='text-sm leading-normal tracking-[-0.084px] text-gray-2'
                            dangerouslySetInnerHTML={{ __html: item.description }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {strategicLocation?.cardImageFiles && strategicLocation?.contentImageFiles && (
            <div
              className='relative h-[180px] bg-none bg-cover bg-center bg-no-repeat md:h-auto md:flex-1 lg:h-[250px] lg:flex-none lg:bg-none'
              style={{
                backgroundImage: `url(${strategicLocation?.cardImageFiles.tablet.url})`,
              }}
            >
              <div className='absolute -inset-2 z-10 bg-gradient-to-b from-white to-black/0' />
              <ImageByWidthDevice
                images={strategicLocation?.cardImageFiles}
                alt='Card Background'
                className='z-0 max-h-[180px] w-full object-fill object-center md:hidden lg:block lg:max-h-[250px]'
              />

              <div className='absolute top-1/2 z-[11] flex w-full -translate-y-1/2 items-center justify-center'>
                <div className='relative left-10 flex flex-1 items-center justify-center sml:left-13 lg:left-12.5'>
                  <div className='z-20 flex size-[100px] items-center justify-center rounded-full bg-white md:size-[108px] lg:size-[142px]'>
                    {strategicLocation?.contentImageFiles[2] && (
                      <ImageByWidthDevice
                        images={strategicLocation?.contentImageFiles[2]}
                        alt='Card Image 2'
                        className='size-[90px] rounded-full object-cover object-center md:size-[98px] lg:size-[132px]'
                      />
                    )}
                  </div>
                </div>

                <div className='flex flex-1 items-center justify-center'>
                  <div className='relative z-30 flex size-[130px] items-center justify-center rounded-full bg-white md:size-[127px] lg:size-[168px]'>
                    {strategicLocation?.contentImageFiles[0] && (
                      <ImageByWidthDevice
                        images={strategicLocation?.contentImageFiles[0]}
                        alt='Card Image 1'
                        className='size-[120px] rounded-full object-cover object-center md:size-[117px] lg:size-[158px]'
                      />
                    )}
                  </div>
                </div>

                <div className='relative right-10 flex flex-1 items-center justify-center sml:right-13 lg:right-12.5'>
                  <div className='z-20 flex size-[100px] items-center justify-center rounded-full bg-white md:size-[108px] lg:right-12 lg:size-[142px]'>
                    {strategicLocation?.contentImageFiles[1] && (
                      <ImageByWidthDevice
                        images={strategicLocation?.contentImageFiles[1]}
                        alt='Card Image 3'
                        className='size-[90px] rounded-full object-cover object-center md:size-[98px] lg:size-[132px]'
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {naturalResources?.cardImageFiles && (
            <div
              className='absolute bottom-0 z-0 h-[167px] w-full bg-none bg-cover bg-center bg-no-repeat md:relative md:h-auto md:flex-1 lg:absolute lg:h-[269px] lg:flex-none lg:bg-none'
              style={{
                backgroundImage: `url(${naturalResources?.cardImageFiles.tablet.url})`,
              }}
            >
              <div className='absolute -inset-2 z-10 bg-gradient-to-b from-white to-black/0' />
              <ImageByWidthDevice
                images={naturalResources?.cardImageFiles}
                alt='Card Background'
                className='z-0 h-full w-full object-cover object-bottom md:hidden md:object-top lg:block'
              />
            </div>
          )}
          {population?.cardImageFiles && (
            <div
              className='absolute bottom-0 z-0 h-[180px] w-full bg-cover bg-center bg-no-repeat md:relative md:h-auto md:flex-1 lg:absolute lg:h-[269px] lg:flex-none lg:bg-none'
              style={{
                backgroundImage: `url(${population?.cardImageFiles.tablet.url})`,
              }}
            >
              <div className='absolute inset-0 z-50' />
              <div className='absolute -inset-2 z-10 bg-gradient-to-b from-white to-black/0' />

              <ImageByWidthDevice
                images={population?.cardImageFiles}
                alt='Card Background'
                className='z-0 h-full w-full object-cover object-top md:hidden lg:block'
              />
            </div>
          )}
        </div>
      </motion.div>
      {legendImageFiles && (
        <ImageByWidthDevice
          images={legendImageFiles}
          alt='legends'
          className={clsxm('map-legends', active ? 'hidden lg:block' : 'hidden')}
        />
      )}
    </>
  )
}

export default MapContent
